import { useState, useEffect, useCallback, useRef } from "react";
import { feedApi } from "../services/api/feedApi";

/**
 * PERFORMANCE-OPTIMIZED FEED HOOK
 * Features:
 * - Cursor-based pagination
 * - Automatic prefetching
 * - Cache management
 * - Error handling
 */
export const useFeed = ({
  filter = null,
  feedType = "personalized",
  topic = null,
  initialLoad = true,
} = {}) => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [hasMore, setHasMore] = useState(true);
  const [cursor, setCursor] = useState(null);
  const loadingRef = useRef(false);

  // Load initial feed
  const loadFeed = useCallback(
    async (reset = false) => {
      if (loadingRef.current) return;

      loadingRef.current = true;
      setLoading(true);
      setError(null);

      try {
        const response = await feedApi.getFeed({
          cursor: reset ? null : cursor,
          filter,
          feedType,
          topic,
          limit: 10,
          optimized: true,
        });

        const { posts: newPosts, pagination } = response.data;

        setPosts((prev) => (reset ? newPosts : [...prev, ...newPosts]));
        setHasMore(pagination.hasMore);
        setCursor(pagination.nextCursor);

        // PERFORMANCE: Prefetch next page in background
        if (pagination.nextCursor && pagination.hasMore) {
          feedApi.prefetchNextPage(pagination.nextCursor).catch(() => {
            // Silent fail - prefetch is optimization only
          });
        }
      } catch (err) {
        setError(err.response?.data?.message || "Failed to load feed");
      } finally {
        setLoading(false);
        loadingRef.current = false;
      }
    },
    [cursor, filter, feedType, topic]
  );

  // Load more posts (infinite scroll)
  const loadMore = useCallback(() => {
    if (!loadingRef.current && hasMore && cursor) {
      loadFeed();
    }
  }, [hasMore, cursor, loadFeed]);

  // Refresh feed (pull to refresh)
  const refresh = useCallback(() => {
    setCursor(null);
    loadFeed(true);
  }, [loadFeed]);

  // Add new post to feed (after creation)
  const addPost = useCallback((newPost) => {
    setPosts((prev) => [newPost, ...prev]);
  }, []);

  // Update post in feed (after edit)
  const updatePost = useCallback((postId, updates) => {
    setPosts((prev) =>
      prev.map((post) => (post._id === postId ? { ...post, ...updates } : post))
    );
  }, []);

  // Remove post from feed (after delete)
  const removePost = useCallback((postId) => {
    setPosts((prev) => prev.filter((post) => post._id !== postId));
  }, []);

  // Update follow status for ALL posts by the same author (batch update)
  // This ensures consistency when following/unfollowing from any post
  const updateFollowStatusForAuthor = useCallback((authorId, isFollowing) => {
    setPosts((prev) =>
      prev.map((post) =>
        post.author?._id === authorId
          ? { ...post, isFollowingAuthor: isFollowing }
          : post
      )
    );
  }, []);

  // Initial load and reload on filter changes
  useEffect(() => {
    // Reset cursor and posts when filters change
    setCursor(null);
    setPosts([]);
    setHasMore(true);

    // Load fresh data
    const loadData = async () => {
      if (loadingRef.current) return;

      loadingRef.current = true;
      setLoading(true);
      setError(null);

      try {
        const response = await feedApi.getFeed({
          cursor: null,
          filter,
          feedType,
          topic,
          limit: 10,
          optimized: true,
        });

        const { posts: newPosts, pagination } = response.data;

        setPosts(newPosts);
        setHasMore(pagination.hasMore);
        setCursor(pagination.nextCursor);

        // PERFORMANCE: Prefetch next page in background
        if (pagination.nextCursor && pagination.hasMore) {
          feedApi.prefetchNextPage(pagination.nextCursor).catch(() => {
            // Silent fail - prefetch is optimization only
          });
        }
      } catch (err) {
        setError(err.response?.data?.message || "Failed to load feed");
      } finally {
        setLoading(false);
        loadingRef.current = false;
      }
    };

    loadData();
  }, [filter, feedType, topic]);

  return {
    posts,
    loading,
    error,
    hasMore,
    loadMore,
    refresh,
    addPost,
    updatePost,
    removePost,
    updateFollowStatusForAuthor,
  };
};
